import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmailCaptureForm } from "@/components/EmailCaptureForm";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { getIconComponent } from "@/utils/iconMapper";
import { useToast } from "@/hooks/use-toast";

interface Resource {
  id: string;
  title: string;
  description: string;
  resource_type: string;
  icon: any;
  category: string;
  filename: string;
  downloadUrl?: string;
}

const categories = ["All", "Planning", "Compliance", "Tools", "Education"];

const Resources = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('is_published', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      if (data) {
        const mappedResources = data.map(r => ({
          id: r.id,
          title: r.title,
          description: r.description,
          resource_type: r.resource_type,
          icon: getIconComponent(r.icon_name),
          category: r.category,
          filename: r.filename,
        }));
        setResources(mappedResources);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      toast({
        title: "Error loading resources",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = selectedCategory === "All" 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  const handleResourceClick = async (resource: Resource) => {
    // Admin bypass - generate signed URL and download directly
    if (isAdmin) {
      try {
        const { data, error } = await supabase.storage
          .from('resources')
          .createSignedUrl(resource.filename, 3600); // 1 hour expiry

        if (error) throw error;
        if (data?.signedUrl) {
          window.open(data.signedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error generating download URL:', error);
        toast({
          title: "Download failed",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      return;
    }
    
    // Regular user - show email capture dialog
    setSelectedResource(resource);
    setIsDialogOpen(true);
  };

  const handleDownloadSuccess = (email: string) => {
    console.log("Download initiated for:", email);
    setTimeout(() => {
      setIsDialogOpen(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Free Cyber Security Resources & Checklists | 4FOX Security"
        description="Download free cyber security guides, templates, and checklists. SME security checklist, incident response plans, risk assessment guides, and compliance frameworks."
        canonical="https://4fox.uk/resources"
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Free Cyber Security Resources
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Practical guides, templates, and tools to help you improve your organisation's security posture.
            </p>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Resources Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">No resources found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredResources.map((resource) => {
                const Icon = resource.icon;
                return (
                  <Card 
                    key={resource.id} 
                    className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full" 
                    onClick={() => handleResourceClick(resource)}
                  >
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                        <Icon className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">{resource.title}</CardTitle>
                      <CardDescription>{resource.resource_type}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">{resource.description}</p>
                      <Button variant="outline" className="w-full">
                        <Download className="mr-2 h-4 w-4" />
                        {isAdmin ? "Download (Admin)" : "Download Free"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* CTA Section */}
          <div className="bg-muted rounded-lg p-8 text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">
              Need Personalized Guidance?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our resources are a great starting point, but every organisation is unique. 
              Book a free consultation to discuss your specific security needs.
            </p>
            <a 
              href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg">
                Book Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </main>

      {/* Download Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download {selectedResource?.title}</DialogTitle>
            <DialogDescription>
              Enter your email to receive this resource instantly. We'll also send you occasional security tips and updates.
            </DialogDescription>
          </DialogHeader>
          {selectedResource && (
            <EmailCaptureForm
              resourceName={selectedResource.title}
              resourceType={selectedResource.category.toLowerCase()}
              onSuccess={handleDownloadSuccess}
              resourceFilename={selectedResource.filename}
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Resources;
